import styled from "@emotion/styled";
import React, { useMemo, useRef } from "react";
import {
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import TablePagination, {
    PAGINATION_AMOUNTS,
} from "~/components/TablePagination";

import ErrorBoundary from "~/components/ErrorBoundary";

import FaIcon from "~/components/FaIcon";
import { TableWrap, StyledTable, Th, Tr, Td } from "~/components/core/Table";
import { ScreenReaderText } from "~/components/ProductTableMain";

import useTableLayoutHelpers from "~/hooks/useTableLayoutHelpers";
import useStringTranslation from "~/hooks/translations/useStringTranslation";

const InnerTh = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const IconWrapper = styled.button`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

export const SortIcon = styled(FaIcon)`
    font-size: 1rem;
    margin-left: 5px;
    color: white;
`;

const Table = ({
    columns,
    data,
    smallTableHeaders,
    tableOptions,
    fixed,
    showPagination,
    onPageChange,
    onPageSizeChange,
}) => {
    const memoColumns = useMemo(() => columns, [columns]);
    const memoData = useMemo(() => data, [data]);

    const tableWrapperRef = useRef();
    const { paginationHeight, paginationWrapperRef } = useTableLayoutHelpers();

    const {
        descendingText,
        ascendingText,
        sortDescendingText,
        sortAscendingText,
        clearSortText,
    } = useStringTranslation([
        { text: "Descending", handle: "descendingText" },
        { text: "Ascending", handle: "ascendingText" },
        { text: "Sort descending", handle: "sortDescendingText" },
        { text: "Sort Ascending", handle: "sortAscendingText" },
        { text: "Clear sort", handle: "clearSortText" },
    ]);

    const {
        getHeaderGroups,
        getRowModel,
        getState,
        getCanPreviousPage,
        getCanNextPage,
        nextPage,
        getPageCount,
        previousPage,
        setPageSize,
        getPageOptions,
        setPageIndex,
    } = useReactTable({
        data: memoData,
        columns: memoColumns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        manualPagination: !!tableOptions?.manualPagination,
        pageCount: tableOptions?.pageCount || -1,
        state: {
            pagination: {
                pageSize: tableOptions?.pageSize || PAGINATION_AMOUNTS[0],
                pageIndex: tableOptions?.pageIndex || 0,
            },
        },
    });

    const rows = getRowModel().rows;
    const headerGroups = getHeaderGroups();

    const onPageIndexChange = (index) => {
        if (typeof onPageChange === "function") {
            onPageChange(index);
        }
    };

    return (
        <ErrorBoundary>
            <TableWrap ref={tableWrapperRef} {...{ paginationHeight }}>
                <StyledTable {...{ fixed }} aria-labelledby="tableLabel">
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    const canSort = header.column.getCanSort();
                                    const isSorted =
                                        header.column.getIsSorted();

                                    let title;

                                    if (canSort) {
                                        if (
                                            header.column.getNextSortingOrder() ===
                                            "asc"
                                        ) {
                                            title = sortAscendingText;
                                        } else if (
                                            header.column.getNextSortingOrder() ===
                                            "desc"
                                        ) {
                                            title = sortDescendingText;
                                        } else {
                                            title = clearSortText;
                                        }
                                    }

                                    return (
                                        <Th
                                            key={header.id}
                                            {...{ smallTableHeaders }}
                                            style={
                                                header.column.columnDef.style
                                            }
                                            title={title}
                                            onClick={header.column.getToggleSortingHandler()}
                                            aria-sort={
                                                isSorted
                                                    ? isSorted === "desc"
                                                        ? descendingText
                                                        : ascendingText
                                                    : null
                                            }
                                        >
                                            <InnerTh>
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                                {!canSort ||
                                                rows.length < 2 ? null : (
                                                    <IconWrapper>
                                                        <ScreenReaderText aria-live="polite">
                                                            {`Sort by ${header.column.columnDef.header}${
                                                                isSorted
                                                                    ? `: ${
                                                                          isSorted ===
                                                                          "desc"
                                                                              ? descendingText
                                                                              : ascendingText
                                                                      }`
                                                                    : ""
                                                            }`}
                                                        </ScreenReaderText>
                                                        {isSorted ? (
                                                            isSorted ===
                                                            "desc" ? (
                                                                <SortIcon name="chevron-down" />
                                                            ) : (
                                                                <SortIcon name="chevron-up" />
                                                            )
                                                        ) : (
                                                            <>
                                                                <SortIcon name="chevron-up" />
                                                                <SortIcon name="chevron-down" />
                                                            </>
                                                        )}
                                                    </IconWrapper>
                                                )}
                                            </InnerTh>
                                        </Th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {rows.map((row, i) => {
                            return (
                                <Tr key={row.id}>
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </Td>
                                        );
                                    })}
                                </Tr>
                            );
                        })}
                    </tbody>
                </StyledTable>
            </TableWrap>

            {showPagination ? (
                <div ref={paginationWrapperRef}>
                    <TablePagination
                        {...{
                            canPreviousPage: getCanPreviousPage(),
                            canNextPage: getCanNextPage(),
                            gotoPage: setPageIndex,
                            pageCount: getPageCount(),
                            pageOptions: getPageOptions(),
                            nextPage: nextPage,
                            previousPage: previousPage,
                            onPageIndexChange,
                            onPageSizeChange,
                            setPageSize,
                            tableOptions,
                            tableWrapperRef,
                        }}
                        pageIndex={getState().pagination.pageIndex}
                        pageSize={getState().pagination.pageSize}
                        scrollToRef={tableWrapperRef.current}
                    />
                </div>
            ) : null}
        </ErrorBoundary>
    );
};

export default Table;
